import { ConvexError, v } from "convex/values";
import { Doc, Id } from "./_generated/dataModel";
import { appFields, userFields } from "./schema";
import { internalAction, internalQuery, query } from "./_generated/server";

import { VideoStats } from "./socials";
import { getAuthUserId } from "@convex-dev/auth/server";

// Helper function to validate if a string is a valid storage ID
// Example valid storage ID: kg277q1d45bh7qytk5xc4cby6n7jf3xk
export function isValidStorageId(id: string): boolean {
  // Storage IDs are typically lowercase alphanumeric strings of specific length
  // Based on example: kg277q1d45bh7qytk5xc4cby6n7jf3xk (34 characters, lowercase letters and numbers only)
  const storageIdPattern = /^[a-z0-9]+$/;
  return storageIdPattern.test(id) && id.length >= 30 && id.length <= 40;
}

// Helper function to normalize social media video URLs
export function normalizeVideoUrl(url: string): string {
  try {
    const urlObj = new URL(url);

    // TikTok URL normalization
    if (urlObj.hostname.includes("tiktok.com")) {
      // Extract video ID from /video/(\d+) pattern
      const videoIdMatch = urlObj.pathname.match(/\/video\/(\d+)/);
      if (videoIdMatch) {
        const videoId = videoIdMatch[1];
        // Extract username from /@([^/]+) pattern
        const usernameMatch = urlObj.pathname.match(/@([^/]+)/);
        if (usernameMatch) {
          const username = usernameMatch[1];
          return `https://www.tiktok.com/@${username}/video/${videoId}`;
        }
      }
      // Return original URL if we can't parse it properly
      return url;
    }

    // Instagram URL normalization
    if (urlObj.hostname.includes("instagram.com")) {
      // Extract post/reel ID from /p/([^/]+) or /reel/([^/]+) pattern
      const postIdMatch = urlObj.pathname.match(/\/(p|reel)\/([^/?]+)/);
      if (postIdMatch) {
        const postId = postIdMatch[2];
        return `https://www.instagram.com/p/${postId}/`;
      }
      // Return original URL if we can't parse it properly
      return url;
    }

    // YouTube URL normalization
    if (
      urlObj.hostname.includes("youtube.com") ||
      urlObj.hostname.includes("youtu.be")
    ) {
      let videoId = "";

      if (urlObj.hostname === "youtu.be") {
        // Short URL format: https://youtu.be/VIDEO_ID
        videoId = urlObj.pathname.slice(1).split("?")[0];
      } else if (urlObj.pathname.includes("/shorts/")) {
        // YouTube Shorts format: https://www.youtube.com/shorts/VIDEO_ID or https://youtube.com/shorts/VIDEO_ID
        videoId = urlObj.pathname.split("/shorts/")[1].split("?")[0];
      } else {
        // Regular YouTube URL: https://www.youtube.com/watch?v=VIDEO_ID
        videoId = urlObj.searchParams.get("v") || "";
      }

      if (videoId) {
        return `https://www.youtube.com/watch?v=${videoId}`;
      }
      // Return original URL if we can't parse it properly
      return url;
    }

    // For other platforms or unrecognized URLs, return as-is
    return url;
  } catch (error) {
    // If URL parsing fails, return the original URL
    return url;
  }
}

// Internal action to resolve URL redirects (especially for TikTok short URLs)
export const resolveUrlRedirects = internalAction({
  args: {
    url: v.string(),
  },
  handler: async (_, args) => {
    try {
      const urlObj = new URL(args.url);

      // Only resolve redirects for TikTok short URLs (vm.tiktok.com)
      if (urlObj.hostname === "vm.tiktok.com") {
        const response = await fetch(args.url, {
          method: "HEAD", // Use HEAD to avoid downloading content
          redirect: "follow",
        });

        if (response.ok && response.url !== args.url) {
          // Return the resolved URL
          return response.url;
        }
      }

      // For all other URLs, return unchanged
      return args.url;
    } catch (error) {
      console.error("Error resolving URL redirect:", error);
      // If redirect resolution fails, return original URL
      return args.url;
    }
  },
});

// We ask for the accountType because we want to show the
// appropriate data on the frontend. If we don't get the accountType,
// we may allow the user to see something only his founder account should see and vice versa.
// Even though the user should have access to both, we want to show the appropriate data.
export const isCampaignFounderOrCampaignCreator = query({
  args: {
    accountType: v.union(
      v.literal("creator"),
      v.literal("brand"),
      v.literal("team"),
    ),
    campaignId: v.optional(v.id("campaigns")),
  },
  handler: async (ctx, args) => {
    if (!args.campaignId) {
      return false;
    }

    const userId = await getAuthUserId(ctx);

    if (!userId) {
      throw new ConvexError("Unauthorized");
    }

    const campaign = await ctx.db.get(args.campaignId);
    if (!campaign) {
      throw new ConvexError("Campaign not found");
    }

    const app = await ctx.db.get(campaign.appId);
    if (!app) {
      throw new ConvexError("App not found");
    }

    if (args.accountType === "creator") {
      const creator = await ctx.db
        .query("creators")
        .withIndex("by_user", (q) => q.eq("userId", userId))
        .first();

      if (!creator) {
        throw new ConvexError("Creator not found");
      }

      const isCampaignCreator = await ctx.db
        .query("creatorContracts")
        .withIndex("by_campaign_and_creator", (q) =>
          q
            .eq("campaignId", args.campaignId as Id<"campaigns">)
            .eq("creatorId", creator._id),
        )
        .first();

      return isCampaignCreator ? isCampaignCreator.creatorId : false;
    } else {
      // Both "brand" and "team" are treated as founders/brand side
      const isCampaignFounder = await ctx.db
        .query("appFounders")
        .withIndex("by_user_and_app", (q) =>
          q.eq("userId", userId).eq("appId", app._id),
        )
        .first();

      return isCampaignFounder ? isCampaignFounder.userId : false;
    }
  },
});

export const getCampaignCreatorStats = internalQuery({
  args: {
    campaignId: v.id("campaigns"),
  },
  handler: async (ctx, args) => {
    // Get all creator contracts for this campaign
    const contracts = await ctx.db
      .query("creatorContracts")
      .withIndex("by_campaign", (q) => q.eq("campaignId", args.campaignId))
      .collect();

    const totalContracts = contracts.length;

    // Get all creators who have posted at least one video
    const creatorsWithVideos = new Set();

    for (const contract of contracts) {
      const hasVideo = await ctx.db
        .query("contractVideos")
        .withIndex("by_contract", (q) => q.eq("contractId", contract._id))
        .first();

      if (hasVideo) {
        creatorsWithVideos.add(contract.creatorId);
      }
    }

    return {
      totalContracts,
      creatorsWithVideos: creatorsWithVideos.size,
    };
  },
});

export const getAppLogo = internalQuery({
  args: { ...appFields, _id: v.id("apps"), _creationTime: v.number() },
  handler: async (ctx, args) => {
    const logo = args.logo
      ? args.logo.includes("https://")
        ? args.logo
        : isValidStorageId(args.logo)
          ? ((await ctx.storage.getUrl(args.logo as Id<"_storage">)) ??
            `https://api.dicebear.com/9.x/shapes/svg?seed=${args.logo}`)
          : `https://api.dicebear.com/9.x/shapes/svg?seed=${args.logo}`
      : `https://api.dicebear.com/9.x/shapes/svg?seed=${args.logo}`;

    return logo;
  },
});

export const getUserAvatar = internalQuery({
  args: { ...userFields, _id: v.id("users"), _creationTime: v.number() },
  handler: async (ctx, args) => {
    const avatar = args.image
      ? args.image.includes("https://")
        ? args.image
        : isValidStorageId(args.image)
          ? ((await ctx.storage.getUrl(args.image as Id<"_storage">)) ??
            `https://api.dicebear.com/9.x/lorelei/svg?seed=${args._id}`)
          : `https://api.dicebear.com/9.x/lorelei/svg?seed=${args._id}`
      : `https://api.dicebear.com/9.x/lorelei/svg?seed=${args._id}`;

    return avatar;
  },
});

// Internal query to get file URL from storage for AI evaluation
export const getFileUrl = internalQuery({
  args: {
    storageId: v.id("_storage"),
  },
  handler: async (ctx, args) => {
    return await ctx.storage.getUrl(args.storageId);
  },
});

export const calculateVideoRequirements = (
  contract: Doc<"creatorContracts">,
  videos: Doc<"contractVideos">[],
) => {
  if (contract.requiredVideos) {
    const { count, duration } = contract.requiredVideos;
    const contractDurationInDays = Math.ceil(
      (contract.endDate - contract.startDate) / (1000 * 60 * 60 * 24),
    );
    const numberOfPeriods = Math.ceil(contractDurationInDays / duration);
    const totalRequired = numberOfPeriods * count;
    return {
      totalRequired,
      postsLeft: Math.max(0, totalRequired - videos.length),
    };
  }

  // If neither field is set, return null to indicate no requirement
  return null;
};

export const calculateVideoEarnings = (
  contract: Doc<"creatorContracts">,
  stats: VideoStats,
) => {
  const flatFee = contract.flatFee?.perVideo ?? 0;
  const cpm = contract.cpmRate?.type === "perVideo" ? contract.cpmRate.rate : 0;
  const maxPayout = contract.cpmRate?.maxPayout ?? 0;
  const hasAchievedMinimumViewsForVideo =
    stats.views >= (contract.cpmRate?.threshold ?? 0);

  if (!hasAchievedMinimumViewsForVideo) {
    return 0;
  }

  const earnings = maxPayout
    ? Math.min(flatFee + (cpm * stats.views) / 1000, maxPayout)
    : flatFee + (cpm * stats.views) / 1000;

  return isNaN(earnings) ? 0 : earnings;
};

export function calculateEarningsForContract(
  contract: Doc<"creatorContracts">,
  videos: Doc<"contractVideos">[],
) {
  const daysSinceContractHasStarted = Math.floor(
    (Date.now() - contract.startDate) / (1000 * 60 * 60 * 24),
  );

  // we add 1 day to the end date to include the last day of the contract
  const contractDurationInDays = Math.floor(
    (contract.endDate + 24 * 60 * 60 * 1000 - contract.startDate) /
      (1000 * 60 * 60 * 24),
  );

  const daysToCalculate =
    contract.status === "completed" || contract.status === "terminated"
      ? contractDurationInDays
      : daysSinceContractHasStarted;

  const earningsFlatFee = contract.flatFee?.timePeriod
    ? daysToCalculate >= contract.flatFee.timePeriod.days
      ? Math.floor(daysToCalculate / contract.flatFee.timePeriod.days) *
        contract.flatFee.timePeriod.amount
      : 0
    : 0;

  const approvedVideos = videos.filter(
    (video) => video.status === "approved" || !video.status,
  );

  const earningsPerVideoBonus =
    (contract.flatFee?.perVideo ?? 0) * approvedVideos.length;

  const viewsSoFarAcrossAllVideos = approvedVideos.reduce((acc, video) => {
    if (video.views) {
      return acc + video.views;
    }
    return acc;
  }, 0);

  const earningsCPM =
    contract.cpmRate?.type === "perVideo"
      ? approvedVideos.reduce((acc, video) => {
          if (video.earnings) {
            return acc + video.earnings;
          }
          return acc;
        }, 0)
      : contract.cpmRate?.type === "totalViews"
        ? viewsSoFarAcrossAllVideos > contract.cpmRate.threshold
          ? (contract.cpmRate.rate * viewsSoFarAcrossAllVideos) / 1000
          : 0
        : 0;

  return {
    earningsFlatFee, // Base pay (like 50 bucks a month)
    earningsPerVideoBonus, // Bonus per video posted irrespective of views
    earningsCPM, // CPM earnings
  };
}
